import { memo, useContext, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Plus as PlusIcon, ArrowRight, Settings as SettingsIcon } from 'react-feather';
import { AuraChatContext, SideBarExpandedContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext, ChatPageContextDataContext, ChatSettingsToggleContext } from '@trinity/chat-components/components/Chat/context';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { ChatHistory, SidebarArrowToggler, ChatHistoryItemsContext, ChatHistoryManagerContext, ChatHistorySharedContext } from '@trinity/chat-components/components/Chat.History/index.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb.js';
import { HistoryChatCount } from '@trinity/chat-components/components/presentation/History.ChatCount';
import { Constants } from '@trinity/chat-components/components/Theme';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import { RateLimitingContext } from '@trinity/chat-components/containers/RateLimiting/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import { ChatContextType } from '@trinity/chat';

export const ChatSidebar = () => {
  const pageContext = useContext(ChatPageContextDataContext);
  const chat = useContext(ActiveChatSessionContext);
  const [isExpanded, setIsExpanded] = useContext(SideBarExpandedContext);
  const items = useContext(ChatHistoryItemsContext);
  const shared = useContext(ChatHistorySharedContext);
  const historyManager = useContext(ChatHistoryManagerContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const isFullpageChat = useIsFullPageChat();
  const brandIdentifier = useActiveBrandIdentifier();
  const chatWelcomeScreen = useContext(ChatWelcomeScreenContext);
  const { query } = useContext(RateLimitingContext);
  const { canToggleAuraChat, auraChatStatus, setAuraChatStatus } = useContext(AuraChatContext);

  const [settings, toggleSettings] = useContext(ChatSettingsToggleContext);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, [setIsExpanded]);

  const handleNewChat = useCallback(() => {
    setAuraChatStatus(false);
    chatWelcomeScreen?.dismiss();
    if (settings) {
      toggleSettings();
    }

    const context = chat?.context || pageContext;

    const resetLandingCards = context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [setAuraChatStatus, chatWelcomeScreen, settings, toggleSettings, dispatch, chat?.context, pageContext, brandIdentifier]);

  const handleNewAuraChat = useCallback(() => {
    setAuraChatStatus(true);
  }, [setAuraChatStatus]);

  const backToChat = useCallback(() => {
    setAuraChatStatus(false);
  }, [setAuraChatStatus]);

  const historyCount = useMemo(() => items.length + shared.length, [items.length, shared.length]);

  const isRateLimitExceeded = useMemo(() => {
    return query?.data?.allowed === false;
  }, [query?.data?.allowed]);

  return (
    <Root>
      <Wrap isExpanded={isExpanded} isFullPageChat={isFullpageChat}>
        <Content>
          <Collapsed isExpanded={isExpanded}>
            <CollapsedHeader>
              <NewChatButtonCollapsed
                disabled={isRateLimitExceeded}
                onClick={handleNewChat}>
                <StyledCollapsedPlusIcon />
              </NewChatButtonCollapsed>
            </CollapsedHeader>
            {canToggleAuraChat && (
              auraChatStatus ? (
                <InsightsButtonCollapsed onClick={backToChat}>
                  <Lightbulb />
                </InsightsButtonCollapsed>
              ) : (
                <AuraButtonCollapsed onClick={handleNewAuraChat}>
                  <img src={AuraLogo} />
                </AuraButtonCollapsed>
              )
            )}
            <CountWrapper>
              {!historyManager.loading && !auraChatStatus && (
                <StyledHistoryChatCount
                  onClick={toggleExpanded}
                  count={historyCount} />
              )}
            </CountWrapper>
          </Collapsed>
          <Expanded isExpanded={isExpanded}>
            {isFullpageChat && (
              <ExpandedHeader>
                <Text>Conversations</Text>
                <NewChatButtonCollapsed
                  disabled={isRateLimitExceeded}
                  isFullPageChat={isFullpageChat}
                  onClick={handleNewChat}>
                  <StyledCollapsedPlusIcon />
                </NewChatButtonCollapsed>
              </ExpandedHeader>
            )}

            {!isFullpageChat && (
              <ExpandedHeader>
                <NewChatButtonExpanded
                  disabled={isRateLimitExceeded}
                  onClick={handleNewChat}>
                  <StyledExpandedPlusIcon />
                  <NewChatTextExpanded>New Chat</NewChatTextExpanded>
                </NewChatButtonExpanded>
              </ExpandedHeader>
            )}

            {canToggleAuraChat && (
              auraChatStatus ? (
                <InsightsButtonExpanded onClick={handleNewChat}>
                  <span className="text">Insights Chat</span>
                  <ArrowRight size={30} />
                </InsightsButtonExpanded>
              ) : (
                <AuraButtonExpanded onClick={handleNewAuraChat}>
                  <img src={AuraLogo} />
                  <span className="text">Launch Aura</span>
                  <ArrowRight size={30} />
                </AuraButtonExpanded>
              )
            )}

            {/* <ChatHistorySearch /> */}

            {!auraChatStatus && <ChatHistory
              items={items}
              shared={shared} />}
          </Expanded>
        </Content>

        <StyledToggler
          open={isExpanded}
          onClick={toggleExpanded} />
        <Settings onClick={toggleSettings}>
          <SettingsIcon />
        </Settings>
      </Wrap>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;

  border-top-left-radius: ${Constants.Modal.BorderRadius}px;
  border-bottom-left-radius: ${Constants.Modal.BorderRadius}px;

  padding: 15px 0;
  height: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
`;

const Settings = styled.div`
  position: absolute;
  bottom: 0;
  left: 20px;
  background-color: ${({ theme }) => theme.palette.gray.light1};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  cursor: pointer;
`;

export const EmptySidebar = memo(() => (
  <Root>
    <Wrap />
  </Root>
));

const Wrap = styled('div', { shouldForwardProp: shouldForwardProp('isFullPageChat') }) <BarProps>`
  position: relative;
  width: ${props => props.isExpanded ? (props.isFullPageChat ? '350' : Constants.Sidebar.ExpandedWidth) : Constants.Sidebar.Width}px;
  transition: width 500ms;
  height: 100%;
`;

const Text = styled.div`
  margin-right:auto;
  font-size: 28px;
  font-family: ${({ theme }) => theme.fonts.bold};
  margin-bottom: 10px;
`;

const Content = styled.div`
  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow: hidden;

  height: 100%;
`;

const Expanded = styled.div<BarProps>`
  display: ${props => props.isExpanded ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;

  opacity: ${props => props.isExpanded ? 1 : 0};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const Collapsed = styled.div<BarProps>`
  display: ${props => props.isExpanded ? 'none' : 'flex'};
  flex-direction: column;
  align-items: center;

  opacity: ${props => props.isExpanded ? 0 : 1};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const ExpandedHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 0 ${Constants.Sidebar.HorizontalPadding}px;
  margin-bottom: 15px;
  align-items: center;
`;

const CollapsedHeader = styled.div`
  margin-bottom: 15px;
`;

const StyledCollapsedPlusIcon = styled(PlusIcon)`
  color: ${props => props.theme.palette.white.main};
  stroke-width: 3;
  width: 40;
  min-width: 40;
`;

const StyledExpandedPlusIcon = styled(PlusIcon)`
  color: ${props => props.theme.palette.secondary.main};
  stroke-width: 3;
  width: 24;
  min-width: 24;
`;
const NewChatButtonCollapsed = styled('button', { shouldForwardProp: shouldForwardProp('isFullPageChat') }) <BarProps>`

  margin-right: ${props =>
    props.isFullPageChat ? '5px' : '0px'};
  cursor: pointer;
  box-sizing: border-box;
  border-radius: 50%;
  background-color:${({ theme }) => theme.palette.sentiment.primary.main};
  width: ${Constants.Sidebar.IconSize}px;
  height: ${Constants.Sidebar.IconSize}px;
  padding: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: background-color 0.3s;

  &:hover {
    background-color: ${props => props.theme.palette.sentiment.primary.dark};
  }
  &:disabled {
    background-color: ${props => props.theme.palette.gray.light1};
    cursor: not-allowed;
  }
`;

const NewChatButtonExpanded = styled.button`
  border-radius: 25px;
  background-color:${({ theme }) => theme.palette.sentiment.primary.main};
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex: 1;

  &:disabled {
    background-color: ${props => props.theme.palette.gray.light1};
    cursor: not-allowed;
  }
`;

const NewChatTextExpanded = styled.div`
  color: ${props => props.theme.palette.secondary.main};
  font-family: ${props => props.theme.fonts.semiBold};

  display: flex;
  justify-content: center;
  align-items: center;

  flex: 1;
`;

const CountWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: ${Constants.Sidebar.IconSize}px;
  height: ${Constants.Sidebar.IconSize}px;
  box-sizing: border-box;
  margin-top:13px;
`;

const StyledHistoryChatCount = styled(HistoryChatCount)`
  cursor: pointer;
`;

const StyledToggler = styled(SidebarArrowToggler)`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  right: -${Constants.Sidebar.ArrowTogglerSize / 2}px;
`;

type BarProps = {
  isExpanded?: boolean;
  isFullPageChat?: boolean;
};

const AuraButtonCollapsed = styled.button`
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 50%;
  background: linear-gradient(90deg, #4925ff 0%,#5C5DA7 35%, #27AAE1 100%);
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  width: ${Constants.Sidebar.IconSize}px; 
  height: ${Constants.Sidebar.IconSize}px; 
  box-shadow: inset 0 1px 3px 0 #1A113B;

  &:hover {
    transform: scale(1.04);
  }

  &:disabled {
    background: ${({ theme }) => theme.palette.gray.light1};
    cursor: not-allowed;
  }

  img {
    width: 25px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    margin-left: 1px;
  }
`;

const AuraButtonExpanded = styled.button`
  touch-action: manipulation;
  -webkit-appearance: none;
  appearance: none;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 15px;
  padding: 12px 16px;
  background: linear-gradient(90deg, #4925ff 0%,#5C5DA7 35%, #27AAE1 100%);
  color: ${({ theme }) => theme.palette.white.main};
  cursor: pointer;
  width: calc(100% - 27px);
  height: 60px; 
  margin: 0 ${Constants.Sidebar.ItemHorizontalPadding}px;
  margin-bottom: 20px;
  transition-property: transform, background-color, filter;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0.55, 1, 0.15, 1);
  box-sizing: border-box;
  border: none;
  box-shadow: inset 0 1px 3px 0 #1A113B;

  &:hover {
    transform: scale(1.04);
    filter: brightness(1.1);
  }

  &:focus {
    outline-style: solid;
    outline-color: transparent;
  }

  &:focus-visible {
    transform: scale(1.04);
    filter: brightness(1.1);
    text-decoration-line: underline;
    text-decoration-thickness: 0.0625em;
    text-underline-offset: 0.125em;
  }

  &:active {
    transform: scale(0.96);
    filter: brightness(0.9);
  }

  img {
    width: 36px;
    height: 36px;
    margin-right: 15px; 
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }

  .text {
    flex: 1; 
    text-align: left; 
    font-family: ${({ theme }) => theme.fonts.semiBold};
    font-size: 23px; 
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }

  .arrow-icon, .arrowright, svg {
    margin-right: -5px;
    color: ${({ theme }) => theme.palette.white.main};
    font-size: 24px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
`;

const InsightsButtonCollapsed = styled.button`
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.primary.light6};
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: ${Constants.Sidebar.IconSize}px; 
  height: ${Constants.Sidebar.IconSize}px; 

  svg {
    width: 25px;
    height: 25px;
    color: ${({ theme }) => theme.palette.white.main}; 
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
`;

const InsightsButtonExpanded = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px; 
  border-radius: 15px;
  background: ${({ theme }) => theme.palette.primary.light6};
  color: ${({ theme }) => theme.palette.white.main};
  font-family: ${({ theme }) => theme.fonts.semiBold};
  font-size: 18px;
  cursor: pointer;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: calc(100% - 27px); 
  height: 60px; 
  margin: 0 ${Constants.Sidebar.ItemHorizontalPadding}px;
  margin-bottom: 20px;

  .text {
    flex: 1; 
    text-align: left; 
    font-family: ${({ theme }) => theme.fonts.semiBold};
    font-size: 23px; 
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
    margin-left: 30px;
  }

  .arrow-icon, .arrowright, svg {
    margin-top: 3px;
    margin-right: -5px;
    color: ${({ theme }) => theme.palette.white.main};
    font-size: 24px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
`;